import { type ReactNode } from 'react';
import {
  ResponsiveContainer, AreaChart as RAreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import { useChartTheme, resolveSeriesColors } from './theme.js';
import { ChartTooltip } from './ChartTooltip.js';
import type { BaseChartProps } from './_internal/shared-types.js';

export interface AreaChartProps extends BaseChartProps {
  /** Curve type. */
  curveType?: 'linear' | 'monotone' | 'step' | 'natural';
  /** Stack the series. */
  stacked?: boolean;
  /** Fill opacity. Default 0.25. */
  fillOpacity?: number;
}

/** Themed area chart with optional gradient fills + stacking. */
export function AreaChart(props: AreaChartProps): ReactNode {
  const {
    data, dataKey, series,
    h = 280, w = '100%',
    withTooltip = true, withLegend = true,
    withXAxis = true, withYAxis = true, withGrid = true,
    valueFormatter, labelFormatter, tooltipContent,
    curveType = 'monotone', stacked, fillOpacity = 0.25,
    withGradient = true,
    className
  } = props;
  const theme = useChartTheme();
  const resolved = resolveSeriesColors(series, theme);
  const gradId = (name: string) => `rr-area-grad-${name}`;

  return (
    <div className={['rr-chart', 'rr-chart--area', className].filter(Boolean).join(' ')} style={{ width: w, height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <RAreaChart data={data}>
          {withGradient ? (
            <defs>
              {resolved.map((s) => (
                <linearGradient key={s.name} id={gradId(s.name)} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.05} />
                </linearGradient>
              ))}
            </defs>
          ) : null}
          {withGrid ? <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" /> : null}
          {withXAxis ? <XAxis dataKey={dataKey} stroke={theme.axis} fontSize={11} tickFormatter={labelFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
          {withYAxis ? <YAxis stroke={theme.axis} fontSize={11} tickFormatter={valueFormatter as never} tickLine={false} axisLine={{ stroke: theme.grid }} /> : null}
          {withTooltip ? <Tooltip cursor={{ stroke: theme.cursor }} content={(tooltipContent ?? <ChartTooltip formatValue={valueFormatter} formatLabel={labelFormatter} />) as never} /> : null}
          {withLegend ? <Legend wrapperStyle={{ fontFamily: 'var(--rr-font-system)', fontSize: 12, color: theme.axisLabel }} /> : null}
          {resolved.map((s) => (
            <Area
              key={s.name}
              type={curveType}
              dataKey={s.name}
              name={s.label ?? s.name}
              stroke={s.color}
              strokeWidth={2}
              stackId={stacked ? 'stack' : undefined}
              fill={withGradient ? `url(#${gradId(s.name)})` : s.color}
              fillOpacity={withGradient ? 1 : fillOpacity}
              isAnimationActive
            />
          ))}
        </RAreaChart>
      </ResponsiveContainer>
    </div>
  );
}
